import { useContext, useEffect, useMemo, useState, useCallback } from 'react';
import { message } from 'antd';
import AuthenticationContext from '../../../contexts/auth/AuthenticationContext';
import CompanyContext from '../../../contexts/company/CompanyContext';
import SubscriptionContext from '../../../contexts/subscription/SubscriptionContext';
import { getCompanyAiReceptionistList } from '../../../../domain/use_cases/ai-receptionist/getCompanyAiReceptionistList';
import { getCompanyAiReceptionistTotals } from '../../../../domain/use_cases/ai-receptionist/getCompanyAiReceptionistTotals';
import { AiReceptionistListRow, AiReceptionistStats } from '../../../../domain/models/AiReceptionist';
import { buildReceptionistAiRows } from '../types/ReceptionistAiDisplay';

const EMPTY_TOTALS: AiReceptionistStats = { callsCount: 0, callDurationMinutes: 0, opportunitiesCount: 0 };

const useReceptionistAiListPageController = () => {
  const { authentication } = useContext(AuthenticationContext);
  const { activeCompany, users } = useContext(CompanyContext);
  const { subscriptions } = useContext(SubscriptionContext);
  const companyId = activeCompany?.id;

  const [rows, setRows] = useState<AiReceptionistListRow[]>([]);
  const [totalRows, setTotalRows] = useState(0);
  const [totals, setTotals] = useState<AiReceptionistStats>(EMPTY_TOTALS);
  // true from the start: the list fetch is debounced, the empty state must not show meanwhile
  const [isLoading, setIsLoading] = useState(true);
  const [isTotalsLoading, setIsTotalsLoading] = useState(true);
  const [searchValue, setSearchValue] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  const fetchList = useCallback(async () => {
    if (!companyId || !authentication) {
      setRows([]);
      setTotalRows(0);
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    try {
      const response = await getCompanyAiReceptionistList(
        { company_id: companyId, q: searchValue, page: currentPage - 1, hitsPerPage: pageSize },
        authentication,
      );
      setRows(response.rows);
      setTotalRows(response.nbHits);
    } catch (e) {
      const errorMessage = e instanceof Error ? e.message : 'Erreur lors du chargement du réceptionniste IA';
      message.error(errorMessage);
    } finally {
      setIsLoading(false);
    }
  }, [companyId, authentication, searchValue, currentPage, pageSize]);

  // Built on render, not in fetchList: users and subscriptions may load after the list does
  const displayRows = useMemo(
    () => buildReceptionistAiRows({ rows, subscriptions, users }),
    [rows, subscriptions, users],
  );

  // Totals span every subscription of the company: fetched once, never on paging or searching
  const fetchTotals = useCallback(async () => {
    if (!companyId || !authentication) {
      setTotals(EMPTY_TOTALS);
      setIsTotalsLoading(false);
      return;
    }

    setIsTotalsLoading(true);
    try {
      const response = await getCompanyAiReceptionistTotals({ company_id: companyId }, authentication);
      setTotals(response.totals);
    } catch (e) {
      const errorMessage = e instanceof Error ? e.message : 'Erreur lors du chargement des statistiques du réceptionniste IA';
      message.error(errorMessage);
    } finally {
      setIsTotalsLoading(false);
    }
  }, [companyId, authentication]);

  useEffect(() => {
    const timer = setTimeout(() => {
      fetchList();
    }, 400);
    return () => clearTimeout(timer);
  }, [fetchList]);

  useEffect(() => {
    // Synchro avec une source distante : l'etat vient d'un appel reseau, pas du rendu.
    // eslint-disable-next-line react/set-state-in-effect
    fetchTotals();
  }, [fetchTotals]);

  const handleSearchChange = (value: string) => {
    setCurrentPage(1);
    setSearchValue(value);
  };

  const handlePageSizeChange = (size: number) => {
    setCurrentPage(1);
    setPageSize(size);
  };

  return {
    state: {
      rows: displayRows,
      totalRows,
      totals,
      isLoading,
      isTotalsLoading,
      searchValue,
      currentPage,
      pageSize,
    },
    actions: {
      setSearchValue: handleSearchChange,
      setCurrentPage,
      setPageSize: handlePageSizeChange,
      refresh: fetchList,
    },
  };
};

export default useReceptionistAiListPageController;
